/**
 * Settings → Appearance. Lets the user recolor the app palette (primary accent,
 * sidebar, canvas) with live preview + presets, persisted per-user. Writes to
 * userSettings/{uid}.theme; the app reads it via useApplyTheme() in Layout.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../../lib/auth.js';
import {
  type SfpTheme,
  DEFAULT_THEME,
  PRESETS,
  applyTheme,
  loadTheme,
  saveTheme,
} from '../../lib/theme.js';

export function Settings() {
  const { user } = useAuth();
  const [theme, setTheme] = useState<SfpTheme>(DEFAULT_THEME);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    let cancelled = false;
    loadTheme(user?.uid ?? null).then((t) => {
      if (cancelled) return;
      setTheme(t);
      applyTheme(t);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Live preview: apply on every change so the whole app recolors as you tweak.
  function preview(next: SfpTheme) {
    setTheme(next);
    applyTheme(next);
    setStatus('idle');
  }

  async function save() {
    setStatus('saving');
    await saveTheme(user?.uid ?? null, theme);
    setStatus('saved');
  }

  function reset() {
    preview(DEFAULT_THEME);
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-8 space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-wider text-stone-400">Settings</div>
        <h1 className="text-2xl font-semibold text-stone-800">Appearance</h1>
        <p className="mt-1 text-sm text-stone-600">
          Change the app palette. Edits preview live across the app; click Save to keep them.
          The default is the University of Texas scheme.
        </p>
      </header>

      {/* Presets */}
      <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-stone-800">Presets</h2>
        <div className="mt-3 flex flex-wrap gap-3">
          {Object.entries(PRESETS).map(([name, p]) => (
            <button
              key={name}
              type="button"
              onClick={() => preview(p)}
              className="group flex items-center gap-2 rounded-xl border border-stone-200 px-3 py-2 text-left hover:border-stone-400"
            >
              <span className="flex -space-x-1">
                <span className="h-5 w-5 rounded-full ring-1 ring-white" style={{ background: p.sidebar }} />
                <span className="h-5 w-5 rounded-full ring-1 ring-white" style={{ background: p.primary }} />
                <span className="h-5 w-5 rounded-full ring-1 ring-stone-300" style={{ background: p.canvas }} />
              </span>
              <span className="text-xs font-medium text-stone-700">{name}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Individual colors */}
      <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-stone-800">Custom colors</h2>
        <ColorRow
          label="Primary / accent"
          hint="Buttons, brand mark, active highlights"
          value={theme.primary}
          onChange={(v) => preview({ ...theme, primary: v })}
        />
        <ColorRow
          label="Sidebar"
          hint="Left navigation background — pick a dark color so the menu text stays legible"
          value={theme.sidebar}
          onChange={(v) => preview({ ...theme, sidebar: v })}
        />
        <ColorRow
          label="Canvas"
          hint="Page background behind content cards — keep it light"
          value={theme.canvas}
          onChange={(v) => preview({ ...theme, canvas: v })}
        />
      </section>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!loaded || status === 'saving'}
          className="rounded-md bg-[color:var(--sfp-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[color:var(--sfp-primary-hover)] disabled:opacity-50"
        >
          {status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved ✓' : 'Save palette'}
        </button>
        <button
          type="button"
          onClick={reset}
          className="rounded-md border border-stone-300 px-4 py-2 text-sm text-stone-600 hover:bg-stone-50"
        >
          Reset to UT default
        </button>
      </div>
    </div>
  );
}

function ColorRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-3">
      <div>
        <div className="text-sm font-medium text-stone-800">{label}</div>
        <div className="text-xs text-stone-500">{hint}</div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-24 rounded border border-stone-300 px-2 py-1 font-mono text-xs"
        />
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 cursor-pointer rounded border border-stone-300 bg-white p-0.5"
          aria-label={label}
        />
      </div>
    </div>
  );
}
